<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
    <!-- Header -->
    <header class="bg-white shadow-sm border-b">
      <div class="container mx-auto px-4 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <h1 class="text-2xl font-bold text-gray-800">Asya Pasifik Kütüphane</h1>
            <span class="ml-4 text-sm text-gray-600">Kontrol Paneli</span>
          </div>
          
          <div class="flex items-center gap-4">
            <span class="text-sm text-gray-600">
              Hoş geldiniz, {{ authStore.currentUser?.name || authStore.currentUser?.email }}
            </span>
            
            <!-- Header Three Dots Menu -->
            <div class="relative">
              <button
                @click="toggleHeaderDropdown"
                class="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
              >
                <i class="fas fa-ellipsis-vertical"></i>
              </button>
              
              <!-- Header Dropdown Menu -->
              <div
                v-if="showHeaderDropdown"
                class="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20"
                @click.stop
              >
                <div class="py-1">
                  <button
                    @click="handleLogout"
                    class="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center transition-colors"
                  >
                    <i class="fas fa-sign-out-alt mr-3 w-4"></i>
                    Çıkış Yap
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>

    <div class="container mx-auto px-4 py-8">

      <!-- Error Message -->
      <div v-if="appStore.error" class="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl mb-6">
        <i class="fas fa-exclamation-circle mr-2"></i>
        {{ appStore.error }}
      </div>

      <!-- Loading State -->
      <div v-if="appStore.loading" class="text-center py-16">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p class="text-gray-600 mt-4">Kitaplıklarınız yükleniyor...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="appStore.apps.length === 0" class="text-center py-16">
        <div class="text-gray-400 mb-4">
          <i class="fas fa-folder-plus text-6xl"></i>
        </div>
        <h3 class="text-xl font-medium text-gray-600 mb-2">Henüz kitaplık yok</h3>
        <p class="text-gray-500 mb-8">Dosya yönetimi ile başlamak için ilk kitaplığınızı oluşturun.</p>
        <button
          @click="openCreateDialog"
          class="px-8 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all font-medium"
        >
          <i class="fas fa-plus mr-2"></i>
          İlk Kitaplığınızı Oluşturun
        </button>
      </div>

      <!-- Apps Grid -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <div
          v-for="app in appStore.apps"
          :key="app.id"
          @click="openApp(app)"
          class="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:scale-105 border border-gray-100 group"
        >
          <div class="p-8 relative">

            <div class="flex items-center justify-between mb-6">
              <h3 class="text-xl font-semibold text-gray-800 truncate pr-8">
                {{ app.name }}
              </h3>
            </div>
            
            <div class="flex items-center gap-3 mb-6">
              <span 
                v-if="app.isPublished" 
                class="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full font-medium"
              >
                <i class="fas fa-globe mr-2"></i>
                Yayında
              </span>
              <span 
                v-else 
                class="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full font-medium"
              >
                <i class="fas fa-edit mr-2"></i>
                Taslak
              </span>
            </div>
            
            <div class="text-sm text-gray-600 mb-6">
              <div class="flex items-center gap-6">
                <span class="flex items-center">
                  <i class="fas fa-file mr-2 text-blue-500"></i>
                  {{ app._count?.files || 0 }} dosya
                </span>
              </div>
              <div class="mt-3 text-xs text-gray-500">
                Oluşturulma: {{ formatDate(app.createdAt) }}
              </div>
            </div>


            <!-- Hover overlay -->
            <div class="absolute inset-0 bg-blue-600 bg-opacity-0 group-hover:bg-opacity-5 transition-all duration-300 rounded-xl pointer-events-none"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Floating Action Button -->
    <button
      @click="openCreateDialog"
      class="fixed bottom-6 right-6 w-16 h-16 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all z-50 flex items-center justify-center hover:scale-105"
    >
      <i class="fas fa-plus text-xl"></i>
    </button>

    <!-- Create App Dialog -->
    <div
      v-if="showCreateDialog"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click="closeCreateDialog"
    >
      <div
        class="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4"
        @click.stop
      >
        <div class="flex items-center justify-between mb-6">
          <h2 class="text-xl font-semibold text-gray-800">
            <i class="fas fa-plus-circle text-blue-600 mr-3"></i>
            Yeni Kitaplık Oluştur
          </h2>
          <button
            @click="closeCreateDialog"
            class="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <i class="fas fa-times text-xl"></i>
          </button>
        </div>
        
        <form @submit.prevent="handleCreateApp" class="space-y-4">
          <div>
            <input
              v-model="newAppName"
              type="text"
              placeholder="Kitaplık adını girin..."
              required
              class="w-full px-6 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              ref="nameInput"
            >
          </div>
          
          <div class="flex gap-4 pt-2">
            <button
              type="button"
              @click="closeCreateDialog"
              class="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all font-medium"
            >
              İptal
            </button>
            <button
              type="submit"
              :disabled="appStore.loading || !newAppName.trim()"
              class="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
            >
              <span v-if="appStore.loading" class="flex items-center justify-center">
                <i class="fas fa-spinner fa-spin mr-2"></i>
                Oluşturuluyor...
              </span>
              <span v-else>
                <i class="fas fa-plus mr-2"></i>
                Oluştur
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/authStore'
import { useAppStore } from '../stores/appStore'
import type { App } from '../types/api'

const router = useRouter()
const authStore = useAuthStore()
const appStore = useAppStore()

const newAppName = ref<string>('')
const showHeaderDropdown = ref<boolean>(false)
const showCreateDialog = ref<boolean>(false)
const nameInput = ref<HTMLInputElement | null>(null)

// Set document title
document.title = 'AP Kütüphane - Kontrol Paneli'

// Safely get the base URL
const baseUrl = computed((): string => {
  if (typeof window !== 'undefined') {
    return window.location.origin
  }
  return ''
})

const handleLogout = (): void => {
  showHeaderDropdown.value = false
  authStore.logout()
  router.push('/login')
}

const handleCreateApp = async (): Promise<void> => {
  try {
    if (!newAppName.value || !newAppName.value.trim()) {
      return
    }

    // Create app - slug will be auto-generated on backend
    await appStore.createApp({ 
      name: newAppName.value.trim()
    })
    newAppName.value = ''
    showCreateDialog.value = false
  } catch (error) {
    console.error('Failed to create app:', error)
  }
}

const openApp = (app: App): void => {
  router.push(`/app/${app.id}/manage`)
}


const toggleHeaderDropdown = (): void => {
  showHeaderDropdown.value = !showHeaderDropdown.value
}


const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('tr-TR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const openCreateDialog = (): void => {
  showCreateDialog.value = true
  // Focus input after dialog opens
  nextTick(() => {
    nameInput.value?.focus()
  })
}

const closeCreateDialog = (): void => {
  showCreateDialog.value = false
  newAppName.value = ''
}

const handleKeyDown = (event: KeyboardEvent): void => {
  if (event.key === 'Escape' && showCreateDialog.value) {
    closeCreateDialog()
  }
}

// Close dropdown when clicking outside
const handleClickOutside = (event: Event): void => {
  const target = event.target as Element
  if (showHeaderDropdown.value && !target?.closest('.relative')) {
    showHeaderDropdown.value = false
  }
}

// Fetch apps when component mounts
onMounted(async () => {
  try {
    await appStore.fetchApps()
    document.addEventListener('click', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
  } catch (error) {
    console.error('Failed to fetch apps:', error)
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleKeyDown)
})
</script> 